import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { CalendarIcon, ClockIcon, MapPinIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { format, parseISO, differenceInSeconds } from 'date-fns';

interface TournamentData {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  venue: string | null;
  status: string;
}

interface EventItem {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  description: string | null;
}

interface DayGroup {
  date: string;
  events: EventItem[];
}

function useCountdown(targetDate: string) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calc = () => {
      const diff = differenceInSeconds(parseISO(targetDate), new Date());
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return {
        days: Math.floor(diff / 86400),
        hours: Math.floor((diff % 86400) / 3600),
        minutes: Math.floor((diff % 3600) / 60),
        seconds: diff % 60,
      };
    };
    setTimeLeft(calc());
    const interval = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

function CountdownUnit({ value, label, delay }: { value: number; label: string; delay: number }) {
  return (
    <motion.div
      initial={{ scale: 0, rotateY: 90 }}
      animate={{ scale: 1, rotateY: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15, delay }}
      className="flex flex-col items-center"
    >
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
        <div className="absolute inset-0 bg-golf-green/20 backdrop-blur-md rounded-2xl border border-white/10" />
        <AnimatePresence mode="popLayout">
          <motion.span
            key={value}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="relative text-3xl sm:text-4xl font-display font-bold text-white"
          >
            {String(value).padStart(2, '0')}
          </motion.span>
        </AnimatePresence>
      </div>
      <span className="mt-2 text-xs sm:text-sm font-medium text-white/70 uppercase tracking-wider">{label}</span>
    </motion.div>
  );
}

function ScheduleCard({ event, index, dayIndex }: { event: EventItem; index: number; dayIndex: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60, scale: 0.9 }}
      animate={isInView ? { opacity: 1, x: 0, scale: 1 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1, type: 'spring', stiffness: 100 }}
      whileHover={{ scale: 1.02, y: -4 }}
      className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 sm:p-6 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-golf-orange/0 to-golf-orange/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative flex items-start gap-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : {}}
          transition={{ delay: index * 0.1 + 0.2, type: 'spring' }}
          className="flex-shrink-0 w-12 h-12 bg-golf-orange/20 rounded-xl flex items-center justify-center"
        >
          <ClockIcon className="w-6 h-6 text-golf-orange" />
        </motion.div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-display font-semibold text-lg group-hover:text-golf-orange transition-colors duration-300">
            {event.title}
          </h4>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-white/60">
            <span className="flex items-center gap-1">
              <ClockIcon className="w-4 h-4" />
              {event.time}
            </span>
            {event.location && (
              <span className="flex items-center gap-1">
                <MapPinIcon className="w-4 h-4" />
                {event.location}
              </span>
            )}
          </div>
          {event.description && (
            <p className="mt-2 text-sm text-white/50 line-clamp-2">{event.description}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function DaySection({ group, dayIndex }: { group: DayGroup; dayIndex: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  let formattedDate = group.date;
  try {
    formattedDate = format(parseISO(group.date), 'EEEE, MMMM d, yyyy');
  } catch {}

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.6 }}
      className="relative"
    >
      <div className="absolute left-6 top-12 bottom-0 w-px bg-gradient-to-b from-golf-orange/60 via-golf-orange/20 to-transparent" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: dayIndex * 0.15 }}
        className="relative flex items-center gap-4 mb-6"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : {}}
          transition={{ delay: dayIndex * 0.15 + 0.2, type: 'spring', stiffness: 200 }}
          className="w-12 h-12 rounded-full bg-golf-orange flex items-center justify-center shadow-lg shadow-golf-orange/30"
        >
          <CalendarIcon className="w-6 h-6 text-white" />
        </motion.div>
        <h3 className="text-xl sm:text-2xl font-display font-bold text-white">
          {formattedDate}
        </h3>
      </motion.div>

      <div className="ml-12 space-y-3">
        {group.events.map((event, idx) => (
          <ScheduleCard key={event.id} event={event} index={idx} dayIndex={dayIndex} />
        ))}
      </div>
    </motion.div>
  );
}

function FloatingOrb({ delay, size, x, y }: { delay: number; size: number; x: string; y: string }) {
  return (
    <motion.div
      className="absolute rounded-full bg-golf-orange/10 blur-3xl pointer-events-none"
      style={{ width: size, height: size, left: x, top: y }}
      animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 6, repeat: Infinity, delay, ease: 'easeInOut' }}
    />
  );
}

export default function Promo() {
  const [tournament, setTournament] = useState<TournamentData | null>(null);
  const [days, setDays] = useState<DayGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgLogo, setOrgLogo] = useState<string | null>(null);

  const countdown = useCountdown(tournament?.start_date ?? new Date().toISOString());

  useEffect(() => {
    async function load() {
      const { data: tournaments } = await supabase
        .from('tournaments')
        .select('id, name, start_date, end_date, venue, status')
        .in('status', ['upcoming', 'ongoing', 'active'])
        .order('start_date', { ascending: true })
        .limit(1);

      const t = tournaments?.[0] ?? null;
      setTournament(t);

      if (t) {
        const { data: events } = await supabase
          .from('events')
          .select('id, title, date, time, location, description')
          .eq('tournament_id', t.id)
          .order('date')
          .order('time');

        if (events && events.length > 0) {
          const grouped: Record<string, EventItem[]> = {};
          for (const ev of events) {
            if (!grouped[ev.date]) grouped[ev.date] = [];
            grouped[ev.date].push(ev);
          }
          setDays(
            Object.entries(grouped)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, evts]) => ({ date, events: evts }))
          );
        }
      }

      const { data: logo } = await supabase
        .from('logos')
        .select('url')
        .eq('type', 'primary')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (logo?.url) setOrgLogo(logo.url);

      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-golf-gray flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-3 border-golf-orange border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-golf-gray overflow-hidden relative">
      {/* Ambient floating orbs */}
      <FloatingOrb delay={0} size={400} x="10%" y="5%" />
      <FloatingOrb delay={2} size={300} x="70%" y="20%" />
      <FloatingOrb delay={4} size={250} x="30%" y="60%" />
      <FloatingOrb delay={1} size={350} x="80%" y="70%" />

      {/* Hero section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, type: 'spring', stiffness: 80 }}
          className="text-center"
        >
          {orgLogo && (
            <motion.img
              src={orgLogo}
              alt="Tournament Logo"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="h-20 sm:h-28 mx-auto mb-8 object-contain"
            />
          )}

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-display font-bold text-white leading-tight"
          >
            {tournament?.name || 'Tournament'}
          </motion.h1>

          {tournament?.venue && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="mt-4 text-lg sm:text-xl text-white/60 flex items-center justify-center gap-2"
            >
              <MapPinIcon className="w-5 h-5 text-golf-orange" />
              {tournament.venue}
            </motion.p>
          )}

          {tournament?.start_date && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-2 text-base sm:text-lg text-white/50"
            >
              {format(parseISO(tournament.start_date), 'MMMM d')} &ndash; {format(parseISO(tournament.end_date), 'MMMM d, yyyy')}
            </motion.p>
          )}
        </motion.div>

        {/* Countdown */}
        {tournament && differenceInSeconds(parseISO(tournament.start_date), new Date()) > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-12"
          >
            <p className="text-center text-white/50 text-sm uppercase tracking-widest mb-4">Tournament begins in</p>
            <div className="flex gap-4 sm:gap-6">
              <CountdownUnit value={countdown.days} label="Days" delay={1.1} />
              <CountdownUnit value={countdown.hours} label="Hours" delay={1.2} />
              <CountdownUnit value={countdown.minutes} label="Min" delay={1.3} />
              <CountdownUnit value={countdown.seconds} label="Sec" delay={1.4} />
            </div>
          </motion.div>
        )}

        {/* Scroll indicator */}
        {days.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="flex flex-col items-center text-white/40"
            >
              <span className="text-xs uppercase tracking-wider mb-1">Schedule</span>
              <ChevronDownIcon className="w-5 h-5" />
            </motion.div>
          </motion.div>
        )}
      </section>

      {/* Schedule timeline */}
      {days.length > 0 && (
        <section className="relative px-6 sm:px-12 lg:px-24 pb-32 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-white">
              Event Schedule
            </h2>
            <div className="mt-4 h-1 w-20 mx-auto bg-gradient-to-r from-golf-orange to-golf-orange/30 rounded-full" />
          </motion.div>

          <div className="space-y-16">
            {days.map((group, idx) => (
              <DaySection key={group.date} group={group} dayIndex={idx} />
            ))}
          </div>
        </section>
      )}

      {/* No events fallback */}
      {days.length === 0 && !loading && (
        <section className="px-6 pb-32 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="text-white/50 text-lg"
          >
            Schedule details coming soon.
          </motion.p>
        </section>
      )}
    </div>
  );
}
